import { useCallback, useEffect, useState } from "react";
import { useAuth } from "@/context/AuthContext";
import client, { getErrorMessage } from "@/api/client";
import { Tabs, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { AlertTriangle, Fuel, Wrench } from "lucide-react";
import { PhotoThumb } from "./shared/PhotoThumb";
import { NoAccess } from "./shared/NoAccess";
import { PaginationBar } from "@/components/shared/PaginationBar";

const HISTORY_ROLES = ["Operator", "Mechanical"];

// Mirrors mobile service-history.tsx, plus Pagination Across All List
// Screens (Sep 2026): each sub-tab is its own paginated fetch (50
// rows/page, `source` param) instead of one combined fetch — switching
// tabs or machines re-fetches that source's first page.
const SUB_TABS = [
  { key: "service_log", label: "Service Log", Icon: Wrench },
  { key: "diesel_filling", label: "Diesel Filling", Icon: Fuel },
  { key: "breakdown", label: "Breakdown", Icon: AlertTriangle },
];

export default function ServiceHistoryTab() {
  const { user } = useAuth();
  const allowed = HISTORY_ROLES.includes(user?.role);

  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [assets, setAssets] = useState([]);
  const [selectedAssetId, setSelectedAssetId] = useState(null);
  const [entries, setEntries] = useState([]);
  const [activeTab, setActiveTab] = useState("service_log");
  const [page, setPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);

  const load = useCallback(async (assetId, source, pageNum) => {
    setLoading(true);
    setError("");
    try {
      const params = { source, page: pageNum };
      if (assetId) params.asset_id = assetId;
      const res = await client.get("/fleetcare/service-history", { params });
      setAssets(res.data.assets);
      setSelectedAssetId(res.data.selected_asset_id);
      setEntries(res.data.items || []);
      setTotalPages(res.data.total_pages || 1);
      setPage(res.data.page || 1);
    } catch (err) {
      setError(getErrorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    if (allowed) load(null, activeTab, 1);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [allowed]);

  if (!allowed) return <NoAccess />;

  const currentAsset = assets.find((a) => a.asset_id === selectedAssetId);
  const activeLabel = SUB_TABS.find((t) => t.key === activeTab)?.label || "";

  const switchAsset = (assetId) => {
    if (assetId !== selectedAssetId) load(assetId, activeTab, 1);
  };

  const switchTab = (tab) => {
    setActiveTab(tab);
    load(selectedAssetId, tab, 1);
  };

  return (
    <div className="space-y-4" data-testid="service-history-view">
      {assets.length > 1 && (
        <div className="flex gap-2 flex-wrap" data-testid="service-history-asset-switcher">
          {assets.map((a) => (
            <button
              key={a.asset_id}
              data-testid={`service-history-switch-${a.asset_id}`}
              onClick={() => switchAsset(a.asset_id)}
              className={`px-4 py-2 rounded-md border text-sm font-semibold transition-colors ${
                a.asset_id === selectedAssetId
                  ? "bg-amber-600 border-amber-600 text-white"
                  : "bg-white border-slate-200 text-slate-600 hover:border-amber-400"
              }`}
            >
              {a.label}
            </button>
          ))}
        </div>
      )}

      {currentAsset && (
        <p className="text-sm font-semibold text-slate-700" data-testid="service-history-asset-name">
          {currentAsset.label}
        </p>
      )}

      {assets.length > 0 && (
        <Tabs value={activeTab} onValueChange={switchTab}>
          <TabsList data-testid="service-history-tabs">
            {SUB_TABS.map(({ key, label, Icon }) => (
              <TabsTrigger key={key} value={key} data-testid={`service-history-tab-${key}`} className="gap-1.5">
                <Icon className="h-3.5 w-3.5" />
                {label}
              </TabsTrigger>
            ))}
          </TabsList>
        </Tabs>
      )}

      {loading ? (
        <div className="rounded-lg border border-slate-200 bg-white py-12 text-center text-sm text-slate-400" data-testid="service-history-loading">
          Loading...
        </div>
      ) : error ? (
        <div className="rounded-lg border border-red-200 bg-red-50 py-12 text-center text-sm text-red-600" data-testid="service-history-error">
          {error}
        </div>
      ) : assets.length === 0 ? (
        <div className="rounded-lg border border-slate-200 bg-white py-12 text-center text-sm text-slate-500" data-testid="service-history-no-asset">
          No asset is currently assigned to your profile yet.
        </div>
      ) : (
        <>
          <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
            <Table data-testid="service-history-table">
              <TableHeader>
                <TableRow>
                  <TableHead>Date</TableHead>
                  <TableHead>Detail</TableHead>
                  <TableHead>Entered By</TableHead>
                  <TableHead>Remarks</TableHead>
                  <TableHead>Photo</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {entries.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center py-8 text-slate-400" data-testid="service-history-tab-empty">
                      No {activeLabel} history yet for this machine.
                    </TableCell>
                  </TableRow>
                ) : (
                  entries.map((e) => (
                    <TableRow key={`${e.source}-${e.id}`} data-testid={`service-history-entry-${e.id}`}>
                      <TableCell className="whitespace-nowrap">
                        {new Date(e.created_at).toLocaleString("en-IN", { dateStyle: "medium", timeStyle: "short" })}
                      </TableCell>
                      <TableCell className="font-medium text-slate-800">{e.detail}</TableCell>
                      <TableCell>{e.entered_by}</TableCell>
                      <TableCell className="text-slate-500 text-sm">
                        {e.source !== "breakdown" && e.remarks ? e.remarks : "—"}
                      </TableCell>
                      <TableCell>
                        {e.photo_count > 0 ? (
                          <PhotoThumb
                            path={`/fleetcare/service-history/photo/${e.source}/${e.id}/0`}
                            alt={`${e.type_label} photo`}
                            testId={`service-history-photo-${e.id}`}
                            size="h-10 w-10"
                          />
                        ) : (
                          <span className="text-slate-300 text-xs">—</span>
                        )}
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
          <PaginationBar
            page={page}
            totalPages={totalPages}
            onPageChange={(p) => load(selectedAssetId, activeTab, p)}
            testId="service-history-pagination"
          />
        </>
      )}
    </div>
  );
}
